import { createTheme } from '@mui/material/styles';

/**
 * TalentMesh design system.
 *
 * One identity across all three roles. Role is expressed as a small, quiet
 * badge in the sidebar — never as a page-wide colour scheme — so admin,
 * vendor and panelist read as one product rather than three.
 *
 * Everything visual should come from here. Component-level `sx` is for
 * layout (position/flex/size), not for re-inventing colour, radius or type.
 */

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

export const tokens = {
  color: {
    // Neutrals carry a slight cool bias so they sit with the indigo accent
    // instead of reading as generic mid-grey.
    canvas: '#F4F6F8',
    surface: '#FFFFFF',
    surfaceSunken: '#F8FAFC',
    surfaceHover: '#F1F4F8',

    border: '#E2E6EB',
    borderStrong: '#CBD2DA',

    ink: '#151A21',
    inkSecondary: '#59616E',
    inkTertiary: '#8A929E',
    inkDisabled: '#AEB5BF',

    // Deepened from the previous #5c6ac4 so it passes contrast as text and
    // as a button fill, while keeping continuity with the existing product.
    accent: '#3B4EA0',
    accentHover: '#31428A',
    accentSoft: '#EEF1FB',
    accentBorder: '#C9D2F0',

    // Semantic colours are deliberately separate from the accent hue.
    success: '#0B6E4F',
    successSoft: '#E6F4EE',
    warning: '#8A5300',
    warningSoft: '#FDF3E3',
    danger: '#B3261E',
    dangerSoft: '#FDECEA',
    info: '#1B6AA5',
    infoSoft: '#E9F2FA',
  },

  // Exactly three radii, replacing the seven found across the app.
  radius: { sm: 6, md: 8, lg: 12 },

  // Shadows are for things that float above the page (menus, dialogs,
  // popovers) only. Cards and panels use a hairline border instead — flat
  // and precise reads more professional than floating gradient cards.
  shadow: {
    overlay: '0 4px 16px rgba(21, 26, 33, 0.10), 0 1px 3px rgba(21, 26, 33, 0.06)',
    dialog: '0 16px 48px rgba(21, 26, 33, 0.18)',
  },

  // Shell geometry — single source of truth. The three dashboards previously
  // each guessed the header height with a different magic number.
  layout: {
    topBarHeight: 56,
    sidebarWidth: 240,
    sidebarCollapsedWidth: 68,
    contentGutter: 24,
    contentMaxWidth: 1600,
  },

  font: {
    sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
};

const { color: c, radius, shadow, font } = tokens;

// A single status vocabulary. Previously the same candidate status rendered
// in different colours depending on which tab you were looking at.
export const statusTone = {
  // Positive / complete
  open: 'success',
  active: 'success',
  approved: 'success',
  selected: 'success',
  hired: 'success',
  completed: 'success',
  confirmed: 'success',
  available: 'success',
  fulfilled: 'success',
  // In progress / neutral-informational
  scheduled: 'info',
  interviewed: 'info',
  filled: 'info',
  in_progress: 'info',
  under_review: 'info',
  submitted: 'info',
  cv_submitted: 'info',
  in_sourcing: 'info',
  // Waiting / needs attention
  pending: 'warning',
  on_hold: 'warning',
  rescheduled: 'warning',
  deadline_passed: 'warning',
  maybe: 'warning',
  duplicate: 'warning',
  // Negative
  closed: 'danger',
  rejected: 'danger',
  cancelled: 'danger',
  no_show: 'danger',
  blocked: 'danger',
};

export const toneColors = {
  success: { fg: c.success, bg: c.successSoft },
  warning: { fg: c.warning, bg: c.warningSoft },
  danger: { fg: c.danger, bg: c.dangerSoft },
  info: { fg: c.info, bg: c.infoSoft },
  neutral: { fg: c.inkSecondary, bg: c.surfaceHover },
};

/** Resolve any backend status string to a tone. */
export const getStatusTone = (status) =>
  statusTone[String(status || '').toLowerCase().replace(/[\s-]+/g, '_')] || 'neutral';

// Role accent, used only for the small role badge in the sidebar.
export const roleAccent = {
  admin: c.accent,
  manager: c.success,
  vendor: c.warning,
  panelist: c.info,
};

export const roleLabel = {
  admin: 'Administrator',
  manager: 'Hiring Manager',
  vendor: 'Vendor / HR',
  panelist: 'Panelist',
};

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: c.accent, dark: c.accentHover, light: c.accentSoft, contrastText: '#FFFFFF' },
    secondary: { main: c.info, contrastText: '#FFFFFF' },
    success: { main: c.success, light: c.successSoft, contrastText: '#FFFFFF' },
    warning: { main: c.warning, light: c.warningSoft, contrastText: '#FFFFFF' },
    error: { main: c.danger, light: c.dangerSoft, contrastText: '#FFFFFF' },
    info: { main: c.info, light: c.infoSoft, contrastText: '#FFFFFF' },
    background: { default: c.canvas, paper: c.surface },
    text: { primary: c.ink, secondary: c.inkSecondary, disabled: c.inkDisabled },
    divider: c.border,
    action: {
      hover: c.surfaceHover,
      selected: c.accentSoft,
      disabled: c.inkDisabled,
      disabledBackground: c.surfaceHover,
    },
  },

  shape: { borderRadius: radius.md },

  // A dense, deliberate type scale for an operational console — one size per
  // role, rather than the seven interchangeable rem values found in the app.
  typography: {
    fontFamily: font.sans,
    htmlFontSize: 16,
    fontSize: 13,

    // Page title
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.01em' },
    h5: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.005em' },
    // Section title
    h6: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4 },

    subtitle1: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.45 },
    subtitle2: { fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.45 },

    body1: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.55 },
    body2: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5 },

    caption: { fontSize: '0.6875rem', fontWeight: 400, lineHeight: 1.45, color: c.inkTertiary },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    },
    button: { fontSize: '0.8125rem', fontWeight: 500, textTransform: 'none', letterSpacing: 0 },
  },

  components: {
    // -- Global resets ----------------------------------------------------
    MuiCssBaseline: {
      styleOverrides: {
        html: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
        body: { backgroundColor: c.canvas, color: c.ink },
        // Numeric columns should line up.
        'th, td': { fontVariantNumeric: 'tabular-nums' },
        // Quiet, consistent scrollbars instead of four bespoke treatments.
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          background: c.borderStrong,
          borderRadius: 8,
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '*::-webkit-scrollbar-thumb:hover': { background: c.inkTertiary, backgroundClip: 'content-box' },
        // Visible keyboard focus everywhere.
        ':focus-visible': { outline: `2px solid ${c.accent}`, outlineOffset: 2 },
      },
    },

    // -- Surfaces ---------------------------------------------------------
    MuiPaper: {
      // Outlined by default: there are ~28 bare `<Paper>` panels in the app
      // that carried MUI's default drop shadow. Flat-with-a-hairline reads as
      // more precise, but a flat Paper with no border would be invisible on a
      // white surface — so the border has to be the default, not opt-in.
      defaultProps: { elevation: 0, variant: 'outlined' },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { border: `1px solid ${c.border}` },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0, variant: 'outlined' },
      styleOverrides: {
        root: {
          border: `1px solid ${c.border}`,
          borderRadius: radius.md,
          backgroundColor: c.surface,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 16, '&:last-child': { paddingBottom: 16 } },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '14px 16px', borderBottom: `1px solid ${c.border}` },
        title: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4 },
        subheader: { fontSize: '0.75rem', color: c.inkSecondary, marginTop: 2 },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: c.border } } },

    // -- Controls ---------------------------------------------------------
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 34,
          padding: '6px 14px',
          whiteSpace: 'nowrap',
        },
        sizeSmall: { minHeight: 28, padding: '3px 10px', fontSize: '0.75rem' },
        sizeLarge: { minHeight: 40, padding: '8px 18px', fontSize: '0.875rem' },
        outlined: { borderColor: c.borderStrong, '&:hover': { borderColor: c.inkTertiary, backgroundColor: c.surfaceHover } },
        containedPrimary: { '&:hover': { backgroundColor: c.accentHover } },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: radius.sm, color: c.inkSecondary, '&:hover': { backgroundColor: c.surfaceHover, color: c.ink } },
        sizeSmall: { padding: 5 },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { height: 22, borderRadius: radius.sm, fontSize: '0.6875rem', fontWeight: 600 },
        label: { paddingLeft: 8, paddingRight: 8 },
        sizeSmall: { height: 20, fontSize: '0.6875rem' },
        outlined: { borderColor: c.border },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          backgroundColor: c.surface,
          fontSize: '0.8125rem',
          '& fieldset': { borderColor: c.borderStrong },
          '&:hover fieldset': { borderColor: c.inkTertiary },
        },
        input: { padding: '9px 12px' },
        inputSizeSmall: { padding: '6px 10px' },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontSize: '0.8125rem' } } },
    MuiFormHelperText: { styleOverrides: { root: { fontSize: '0.6875rem', marginLeft: 2 } } },
    MuiSelect: { styleOverrides: { select: { fontSize: '0.8125rem' } } },
    MuiMenuItem: { styleOverrides: { root: { fontSize: '0.8125rem', minHeight: 34, borderRadius: radius.sm, margin: '1px 6px' } } },
    MuiMenu: {
      styleOverrides: {
        paper: { border: `1px solid ${c.border}`, borderRadius: radius.md, boxShadow: shadow.overlay, paddingTop: 4, paddingBottom: 4 },
      },
    },
    MuiPopover: {
      styleOverrides: { paper: { border: `1px solid ${c.border}`, borderRadius: radius.md, boxShadow: shadow.overlay } },
    },

    // -- Tables -----------------------------------------------------------
    MuiTableCell: {
      styleOverrides: {
        root: { padding: '10px 14px', borderBottom: `1px solid ${c.border}`, fontSize: '0.8125rem' },
        head: {
          backgroundColor: c.surfaceSunken,
          color: c.inkSecondary,
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          borderBottom: `1px solid ${c.border}`,
        },
        sizeSmall: { padding: '7px 12px' },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:last-of-type td': { borderBottom: 'none' } },
        hover: { '&:hover': { backgroundColor: c.surfaceHover } },
      },
    },
    MuiTableContainer: { styleOverrides: { root: { backgroundColor: c.surface } } },
    MuiTablePagination: {
      styleOverrides: {
        root: { borderTop: `1px solid ${c.border}`, fontSize: '0.75rem' },
        selectLabel: { fontSize: '0.75rem' },
        displayedRows: { fontSize: '0.75rem' },
      },
    },

    // -- Navigation -------------------------------------------------------
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          minHeight: 36,
          paddingTop: 6,
          paddingBottom: 6,
          color: c.inkSecondary,
          '&:hover': { backgroundColor: c.surfaceHover, color: c.ink },
          '&.Mui-selected': {
            backgroundColor: c.accentSoft,
            color: c.accent,
            fontWeight: 600,
            '&:hover': { backgroundColor: c.accentSoft },
            '& .MuiListItemIcon-root': { color: c.accent },
          },
        },
      },
    },
    MuiListItemIcon: { styleOverrides: { root: { minWidth: 32, color: 'inherit' } } },
    MuiListItemText: {
      styleOverrides: { primary: { fontSize: '0.8125rem', fontWeight: 'inherit' } },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40, borderBottom: `1px solid ${c.border}` },
        indicator: { height: 2, borderRadius: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          padding: '0 14px',
          fontSize: '0.8125rem',
          fontWeight: 500,
          textTransform: 'none',
          color: c.inkSecondary,
          '&.Mui-selected': { color: c.accent, fontWeight: 600 },
        },
      },
    },

    // -- Overlays ---------------------------------------------------------
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: radius.lg, boxShadow: shadow.dialog, backgroundImage: 'none' },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { padding: '16px 20px', fontSize: '0.9375rem', fontWeight: 600, borderBottom: `1px solid ${c.border}` },
      },
    },
    MuiDialogContent: { styleOverrides: { root: { padding: 20 } } },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '12px 20px', borderTop: `1px solid ${c.border}`, gap: 8 } },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: c.ink, fontSize: '0.6875rem', fontWeight: 500, borderRadius: radius.sm, padding: '5px 8px' },
        arrow: { color: c.ink },
      },
    },

    // -- Feedback ---------------------------------------------------------
    MuiAlert: {
      defaultProps: { variant: 'standard' },
      styleOverrides: {
        root: { borderRadius: radius.sm, fontSize: '0.8125rem', padding: '8px 14px', border: '1px solid transparent' },
        standardSuccess: { backgroundColor: c.successSoft, color: c.success, borderColor: 'rgba(11,110,79,0.18)' },
        standardWarning: { backgroundColor: c.warningSoft, color: c.warning, borderColor: 'rgba(138,83,0,0.18)' },
        standardError: { backgroundColor: c.dangerSoft, color: c.danger, borderColor: 'rgba(179,38,30,0.18)' },
        standardInfo: { backgroundColor: c.infoSoft, color: c.info, borderColor: 'rgba(27,106,165,0.18)' },
        icon: { opacity: 1 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { height: 3, borderRadius: 3, backgroundColor: c.border } },
    },
    MuiAvatar: { styleOverrides: { root: { fontSize: '0.8125rem', fontWeight: 600 } } },
    MuiBadge: { styleOverrides: { badge: { fontSize: '0.625rem', fontWeight: 600, minWidth: 16, height: 16 } } },
    MuiSkeleton: { styleOverrides: { root: { backgroundColor: c.surfaceHover, borderRadius: radius.sm } } },
  },
});

export default theme;
